import { useState, useEffect, useContext, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Tooltip,
  CircularProgress,
  Switch,
  Tabs,
  Tab,
} from "@mui/material";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import DashboardLayout from "../layouts/DashboardLayout";
import { COLORS } from "../theme/tokens";
import { AuthContext } from "../context/AuthContext";
import FlatCard from "../components/FlatCard";
import TableSkeleton from "../components/TableSkeleton";
import { useToast } from "../components/ToastProvider";
import api from "../api";
import PaginationFooter from "../components/PaginationFooter";
import { useExpiringAlerts, useAlertHistory } from "../hooks/queries";
import { colorForDays, expiresInLabel } from "../utils/expiry";
import { HEADING_FONT } from "../theme/fonts";
import {
  pushSupported,
  isSubscribed,
  enablePush,
  disablePush,
  primeForeground,
} from "../utils/push";

// Channel chip colour
const CHANNEL_COLOR = "#3B82F6";

// A polished on/off pill used for the Push and Email alert toggles. When
// active it gets a soft green gradient, a glowing border and a tinted icon
// badge; when off it stays neutral. `available=false` dims it (e.g. email not
// configured on the server).
function NotifyToggle({
  colors,
  active,
  available = true,
  busy = false,
  disabled = false,
  onChange,
  iconActive,
  iconInactive,
  title,
  statusLabel,
}) {
  const accent = "#10b981";
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        pl: 1,
        pr: 1.5,
        py: 0.75,
        borderRadius: 2.5,
        minWidth: 178,
        minHeight: 52,
        boxSizing: "border-box",
        background: active
          ? "linear-gradient(135deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.03) 100%)"
          : colors.cardBg,
        border: `1px solid ${
          active ? "rgba(16,185,129,0.45)" : colors.cardBorder
        }`,
        boxShadow: active ? "0 0 16px rgba(16,185,129,0.18)" : "none",
        opacity: available ? 1 : 0.55,
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: active
            ? "rgba(16,185,129,0.65)"
            : colors.cardBorderHover,
          transform: available && !disabled ? "translateY(-1px)" : "none",
        },
      }}
    >
      {/* Icon badge */}
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 2,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: active ? "rgba(16,185,129,0.18)" : colors.hover,
          color: active ? accent : colors.textMuted,
          transition: "all 0.2s ease",
        }}
      >
        {active ? iconActive : iconInactive}
      </Box>

      {/* Label + status */}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 700,
            color: colors.text,
            lineHeight: 1.25,
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.6}>
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: active ? accent : colors.textFaint,
              boxShadow: active ? `0 0 6px ${accent}` : "none",
            }}
          />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: active ? accent : colors.textMuted,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            {statusLabel}
          </Typography>
        </Stack>
      </Box>

      {/* Switch / spinner */}
      {busy ? (
        <CircularProgress size={18} sx={{ color: accent, ml: "auto" }} />
      ) : (
        <Switch
          checked={active}
          onChange={onChange}
          disabled={disabled}
          size="small"
          sx={{
            ml: "auto",
            my: -0.5,
            "& .MuiSwitch-switchBase.Mui-checked": { color: accent },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: accent,
            },
          }}
        />
      )}
    </Stack>
  );
}

const EMPTY_BAND_COUNTS = { red: 0, orange: 0, yellow: 0, green: 0 };

// Turn the /alerts/expiring response into { items, total, counts } for the
// current page/band. Backend returns { items, total, counts }; a legacy build
// returns { thresholds, items } (full list) which we paginate + tally here.
function normalizeExpiring(data, band, expPage, expPageSize) {
  if (!data) return { items: [], total: 0, counts: EMPTY_BAND_COUNTS };
  if (data.total !== undefined) {
    return {
      items: data.items || [],
      total: data.total || 0,
      counts: data.counts || EMPTY_BAND_COUNTS,
    };
  }
  // Legacy backend ({ thresholds, items }) — paginate + tally client-side.
  const all = data.items || [];
  const c = { red: 0, orange: 0, yellow: 0, green: 0 };
  all.forEach((a) => {
    c[colorForDays(a.daysLeft).level] =
      (c[colorForDays(a.daysLeft).level] || 0) + 1;
  });
  const filt = all.filter((a) => colorForDays(a.daysLeft).level === band);
  const start = (expPage - 1) * expPageSize;
  return {
    items: filt.slice(start, start + expPageSize),
    total: filt.length,
    counts: c,
  };
}

// Turn the /alerts/history response into { items, total } for the current page.
function normalizeHistory(data, histPage, histPageSize) {
  if (!data) return { items: [], total: 0 };
  if (Array.isArray(data)) {
    // Legacy backend returns the full array — slice client-side.
    const start = (histPage - 1) * histPageSize;
    return { items: data.slice(start, start + histPageSize), total: data.length };
  }
  return { items: data.items || [], total: data.total || 0 };
}

function ExpiryAlerts() {
  // Flat spec design tokens (light-only). Replaces the old glass theme
  // (useThemeColors) so the page is locked to the "Blue · Light" system.
  const colors = {
    text: COLORS.ink,
    textMuted: COLORS.muted,
    textFaint: COLORS.faint,
    cardBg: COLORS.surface,
    cardBorder: COLORS.hairline,
    cardBorderHover: COLORS.borderHover,
    hover: COLORS.tile,
    divider: COLORS.hairline,
  };
  const queryClient = useQueryClient();
  const toast = useToast();
  const { currentUser } = useContext(AuthContext);
  const isAdmin = currentUser?.role !== "user";

  // Pagination — keeps both tables short instead of one long scroll.
  const [expPage, setExpPage] = useState(1);
  const [expPageSize, setExpPageSize] = useState(10);
  // Which colour band the "Expiring Soon" list is filtered to (tab selection).
  const [band, setBand] = useState("red");
  const [histPage, setHistPage] = useState(1);
  const [histPageSize, setHistPageSize] = useState(10);
  const [running, setRunning] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [emailOn, setEmailOn] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);

  // Fetch the current "Expiring Soon" page for the selected band. Backend
  // returns { items, total, counts } (counts are global per band).
  const expParams = useMemo(
    () => ({
      band,
      limit: expPageSize,
      offset: (expPage - 1) * expPageSize,
    }),
    [band, expPage, expPageSize]
  );
  const expiringQuery = useExpiringAlerts(expParams, {
    placeholderData: (prev) => prev,
  });
  const {
    items: expiring,
    total: expTotal,
    counts: bandCounts,
  } = useMemo(
    () => normalizeExpiring(expiringQuery.data, band, expPage, expPageSize),
    [expiringQuery.data, band, expPage, expPageSize]
  );
  const loading = expiringQuery.isFetching;

  // Fetch the current "Alert History" page.
  const histParams = useMemo(
    () => ({
      limit: histPageSize,
      offset: (histPage - 1) * histPageSize,
    }),
    [histPage, histPageSize]
  );
  const historyQuery = useAlertHistory(histParams, {
    placeholderData: (prev) => prev,
  });
  const { items: history, total: histTotal } = useMemo(
    () => normalizeHistory(historyQuery.data, histPage, histPageSize),
    [historyQuery.data, histPage, histPageSize]
  );
  const histLoading = historyQuery.isFetching;

  // Surface an expiring-list load failure as a toast (matches old behaviour).
  useEffect(() => {
    if (expiringQuery.isError) {
      toast.error(expiringQuery.error?.message || "Failed to load alerts");
    }
  }, [expiringQuery.isError, expiringQuery.error, toast]);

  // Reset to page 1 when the page size changes.
  useEffect(() => {
    setExpPage(1);
  }, [expPageSize]);
  useEffect(() => {
    setHistPage(1);
  }, [histPageSize]);

  useEffect(() => {
    if (pushSupported()) {
      isSubscribed().then((on) => {
        setPushOn(on);
        if (on) primeForeground();
      });
    }
  }, []);

  // Load the global email-alert status (configured + enabled).
  useEffect(() => {
    api
      .get("/alerts/email")
      .then((d) => {
        setEmailConfigured(Boolean(d.configured));
        setEmailOn(Boolean(d.enabled));
      })
      .catch(() => {
        // leave defaults — the toggle just shows as unavailable
      });
  }, []);

  async function handleToggleEmail() {
    const next = !emailOn;
    setEmailBusy(true);
    try {
      const res = await api.put("/alerts/email", { enabled: next });
      setEmailOn(Boolean(res.enabled));
      if (res.enabled) {
        toast.success("Email alerts turned on");
      } else {
        toast.info("Email alerts turned off");
      }
    } catch (err) {
      toast.error(
        err.status === 403
          ? "Only admins can change email alerts"
          : err.message || "Could not update email alerts"
      );
    } finally {
      setEmailBusy(false);
    }
  }

  async function handleTogglePush() {
    setPushBusy(true);
    try {
      if (pushOn) {
        await disablePush();
        setPushOn(false);
        toast.info("Browser notifications turned off");
      } else {
        const res = await enablePush();
        if (res.ok) {
          setPushOn(true);
          toast.success("Browser notifications enabled on this device");
        } else {
          const reasons = {
            unsupported: "This browser doesn't support push notifications",
            "not-configured": "Push is not configured on the server",
            denied: "Permission was blocked — allow notifications in the browser",
          };
          toast.error(
            reasons[res.reason] || `Could not enable push (${res.reason})`
          );
        }
      }
    } finally {
      setPushBusy(false);
    }
  }

  async function handleRunNow() {
    setRunning(true);
    try {
      const res = await api.post("/alerts/run");
      toast.success(
        `Scan complete — ${res.raised} new alert${
          res.raised === 1 ? "" : "s"
        } from ${res.scanned} domains`
      );
      // Refresh both lists via React Query instead of manual re-fetches.
      queryClient.invalidateQueries({ queryKey: ["alerts-expiring"] });
      queryClient.invalidateQueries({ queryKey: ["alerts-history"] });
    } catch (err) {
      toast.error(err.message || "Failed to run scan");
    } finally {
      setRunning(false);
    }
  }

  // Per-band counts (global) come from the backend, used by both the summary
  // strip and the tab badges.
  const counts = bandCounts;
  const totalExpiringAll =
    bandCounts.red + bandCounts.orange + bandCounts.yellow + bandCounts.green;

  const summary = [
    { level: "red", label: "Critical (≤7d)", hex: "#ef4444" },
    { level: "orange", label: "Urgent (≤30d)", hex: "#f97316" },
    { level: "yellow", label: "Warning (≤60d)", hex: "#f59e0b" },
    { level: "green", label: "Healthy (≤90d)", hex: "#10b981" },
  ];

  // Tabs that filter the "Expiring Soon" list by colour band.
  const TABS = [
    { value: "red", label: "Critical", hex: "#ef4444" },
    { value: "orange", label: "Urgent", hex: "#f97316" },
    { value: "yellow", label: "Warning", hex: "#f59e0b" },
    { value: "green", label: "Healthy", hex: "#10b981" },
  ];
  const activeHex = (TABS.find((t) => t.value === band) || TABS[0]).hex;

  const EXP_COLS = "minmax(200px, 2fr) 140px 130px minmax(160px, 1.4fr) 90px";
  const HIST_COLS = "minmax(180px, 2fr) 110px 110px minmax(150px, 1.4fr) 160px";

  // `expiring` and `history` are already just the current page from the
  // backend, so no client-side slicing.
  const expTotalPages = Math.max(1, Math.ceil(expTotal / expPageSize));
  const expSafePage = Math.min(expPage, expTotalPages);
  const pagedExpiring = expiring;
  const histTotalPages = Math.max(1, Math.ceil(histTotal / histPageSize));
  const histSafePage = Math.min(histPage, histTotalPages);
  const pagedHistory = history;

  // Step back if the current page fell past the end (e.g. after a scan).
  useEffect(() => {
    if (expPage > expTotalPages) setExpPage(expTotalPages);
  }, [expPage, expTotalPages]);
  useEffect(() => {
    if (histPage > histTotalPages) setHistPage(histTotalPages);
  }, [histPage, histTotalPages]);

  return (
    <DashboardLayout>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 4, justifyContent: "space-between" }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontFamily: HEADING_FONT, fontWeight: 700, color: colors.text, letterSpacing: "-0.5px" }}
          >
            Expiry Alerts
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textMuted, mt: 0.5 }}>
            Domains approaching expiry and the alerts that have been raised
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ alignSelf: { xs: "flex-end", sm: "center" }, flexWrap: "wrap" }}
        >
          {/* Notification toggles (Push + Email) are intentionally hidden from
              the UI per request. The code is kept (not removed) so the
              underlying notification logic stays intact — email alerts remain
              driven by the server-side global setting and push stays
              subscribed via auto-register on login. Flip `false` to `true` to
              bring the toggles back. */}
          {false && (
            <>
              {pushSupported() && (
                <NotifyToggle
                  colors={colors}
                  active={pushOn}
                  busy={pushBusy}
                  disabled={pushBusy}
                  onChange={handleTogglePush}
                  iconActive={<NotificationsActiveOutlinedIcon sx={{ fontSize: 19 }} />}
                  iconInactive={<NotificationsOffOutlinedIcon sx={{ fontSize: 19 }} />}
                  title="Push Alerts"
                  statusLabel={pushOn ? "Enabled" : "Off"}
                />
              )}

              {/* Email-alert toggle (global, admin-only to change) */}
              <Tooltip
                title={
                  !emailConfigured
                    ? "Configure SMTP + recipients in backend .env to enable email alerts"
                    : !isAdmin
                    ? "Only admins can change email alerts"
                    : emailOn
                    ? "Email alerts are on"
                    : "Email alerts are off"
                }
              >
                <Box sx={{ display: "inline-flex" }}>
                  <NotifyToggle
                    colors={colors}
                    active={emailOn && emailConfigured}
                    available={emailConfigured}
                    busy={emailBusy}
                    disabled={emailBusy || !emailConfigured || !isAdmin}
                    onChange={handleToggleEmail}
                    iconActive={<MarkEmailReadOutlinedIcon sx={{ fontSize: 19 }} />}
                    iconInactive={<EmailOutlinedIcon sx={{ fontSize: 19 }} />}
                    title="Email Alerts"
                    statusLabel={
                      !emailConfigured ? "Not set up" : emailOn ? "Enabled" : "Off"
                    }
                  />
                </Box>
              </Tooltip>
            </>
          )}

          {/* "Run Check Now" button hidden per request. It only manually
              triggered an on-demand scan — automatic alerts still fire every
              30 min via the backend worker, so hiding this changes nothing
              about notification delivery. Flip `false` to `isAdmin` to bring
              it back. */}
          {false && (
            <Button
              onClick={handleRunNow}
              disabled={running}
              startIcon={
                running ? (
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                ) : (
                  <RefreshIcon />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                px: 2.5,
                py: 1.1,
                borderRadius: 2,
                color: "#fff",
                background: "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)",
                boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)",
                "&:hover": { boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)" },
                "&.Mui-disabled": { color: "rgba(255,255,255,0.7)" },
              }}
            >
              {running ? "Scanning…" : "Run Check Now"}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Colour summary strip */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Clicking a band card selects the matching tab on the list below —
            `s.level` already matches the `band` state values 1:1. */}
        {summary.map((s) => {
          const selectBand = () => {
            setBand(s.level);
            setExpPage(1);
          };
          const active = band === s.level;
          return (
            <FlatCard
              key={s.level}
              hover
              role="button"
              tabIndex={0}
              aria-pressed={active}
              onClick={selectBand}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  selectBand();
                }
              }}
              sx={{
                p: 2,
                borderLeft: `4px solid ${s.hex}`,
                ...(active && {
                  borderColor: s.hex,
                  boxShadow: `0 0 0 1px ${s.hex}`,
                }),
              }}
            >
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: s.hex }}>
                {counts[s.level] || 0}
              </Typography>
              <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
                {s.label}
              </Typography>
            </FlatCard>
          );
        })}
      </Box>

      {/* Expiring soon table */}
      <FlatCard sx={{ overflow: "hidden", mb: 4 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ px: 3, py: 2, borderBottom: `1px solid ${colors.divider}` }}
        >
          <AccessTimeIcon sx={{ fontSize: 20, color: "#3B82F6" }} />
          <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 16, fontWeight: 700, color: colors.text }}>
            Domains Expiring Soon
          </Typography>
          <Chip
            label={expTotal}
            size="small"
            sx={{
              height: 22,
              fontWeight: 700,
              background: "rgba(59, 130, 246, 0.12)",
              color: "#3B82F6",
            }}
          />
        </Stack>

        {/* Band filter tabs */}
        <Tabs
          value={band}
          onChange={(_, v) => {
            setBand(v);
            setExpPage(1);
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1.5,
            minHeight: 44,
            borderBottom: `1px solid ${colors.divider}`,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: 13,
              minHeight: 44,
              color: colors.textMuted,
              "&.Mui-selected": { color: colors.text },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: activeHex,
              height: 3,
              borderRadius: 3,
            },
          }}
        >
          {TABS.map((t) => {
            const n = counts[t.value] || 0;
            const selected = band === t.value;
            return (
              <Tab
                key={t.value}
                value={t.value}
                label={
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <span>{t.label}</span>
                    <Box
                      component="span"
                      sx={{
                        ml: 0.25,
                        px: 0.75,
                        py: 0.1,
                        borderRadius: 1,
                        fontSize: 11,
                        fontWeight: 700,
                        color: selected ? "#fff" : t.hex,
                        background: selected ? t.hex : `${t.hex}22`,
                      }}
                    >
                      {n}
                    </Box>
                  </Stack>
                }
              />
            );
          })}
        </Tabs>

        {/* Horizontal scroll so the md+ fixed-column table doesn't overflow the
            page on narrow widths (mirrors the other list pages). */}
        <Box sx={{ overflowX: "auto" }}>
        {/* head */}
        <Box
          sx={{
            display: { xs: "none", md: "grid" },
            gridTemplateColumns: EXP_COLS,
            gap: 2,
            px: 3,
            py: 1.5,
            background: colors.hover,
            borderBottom: `1px solid ${colors.divider}`,
          }}
        >
          {["Domain", "Expiry Date", "Days Left", "Action Required", "Status"].map(
            (h) => (
              <Typography
                key={h}
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: colors.textMuted,
                }}
              >
                {h}
              </Typography>
            )
          )}
        </Box>

        {loading ? (
          <TableSkeleton rows={expPageSize || 8} columns={5} showAvatar={false} />
        ) : expiring.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center", color: colors.textMuted }}>
            <NotificationsActiveOutlinedIcon
              sx={{ fontSize: 40, color: "#10b981", mb: 1 }}
            />
            <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 14, fontWeight: 600, color: colors.text }}>
              {totalExpiringAll === 0
                ? "Nothing expiring soon"
                : "No domains in this band"}
            </Typography>
            <Typography sx={{ fontSize: 13, mt: 0.5 }}>
              {totalExpiringAll === 0
                ? "No domains within the next 90 days"
                : "Try another tab"}
            </Typography>
          </Box>
        ) : (
          pagedExpiring.map((a, idx) => {
            const band = colorForDays(a.daysLeft);
            return (
              <Box
                key={a.domainId ?? idx}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: EXP_COLS },
                  gap: { xs: 0.5, md: 2 },
                  px: 3,
                  py: 1.75,
                  alignItems: "center",
                  borderBottom:
                    idx < pagedExpiring.length - 1
                      ? `1px solid ${colors.divider}`
                      : "none",
                  borderLeft: `4px solid ${band.hex}`,
                  "&:hover": { background: colors.hover },
                  transition: "background 0.18s ease",
                }}
              >
                <Typography
                  sx={{ fontFamily: HEADING_FONT, fontSize: 14, fontWeight: 600, color: colors.text }}
                >
                  {a.domainName}
                </Typography>
                <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted }}>
                  {a.expiryDate || "—"}
                </Typography>
                <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 13, fontWeight: 700, color: band.hex }}>
                  {expiresInLabel(a.daysLeft)}
                </Typography>
                <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.text }}>
                  {a.action}
                </Typography>
                <Box>
                  <Chip
                    label={band.label}
                    size="small"
                    sx={{
                      fontFamily: HEADING_FONT,
                      height: 22,
                      fontWeight: 700,
                      fontSize: 11,
                      color: band.hex,
                      background: band.bg,
                    }}
                  />
                </Box>
              </Box>
            );
          })
        )}
        </Box>

        {!loading && expTotal > expPageSize && (
          <PaginationFooter
            page={expSafePage}
            pageSize={expPageSize}
            total={expTotal}
            onPageChange={setExpPage}
            onPageSizeChange={setExpPageSize}
          />
        )}
      </FlatCard>

      {/* Alert history */}
      <FlatCard sx={{ overflow: "hidden" }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ px: 3, py: 2, borderBottom: `1px solid ${colors.divider}` }}
        >
          <HistoryEduOutlinedIcon sx={{ fontSize: 20, color: "#3B82F6" }} />
          <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 16, fontWeight: 700, color: colors.text }}>
            Alert History
          </Typography>
          <Chip
            label={histTotal}
            size="small"
            sx={{
              height: 22,
              fontWeight: 700,
              background: "rgba(59, 130, 246, 0.12)",
              color: "#3B82F6",
            }}
          />
        </Stack>

        {/* Horizontal scroll so the md+ fixed-column table doesn't overflow. */}
        <Box sx={{ overflowX: "auto" }}>
        <Box
          sx={{
            display: { xs: "none", md: "grid" },
            gridTemplateColumns: HIST_COLS,
            gap: 2,
            px: 3,
            py: 1.5,
            background: colors.hover,
            borderBottom: `1px solid ${colors.divider}`,
          }}
        >
          {["Domain", "Threshold", "Days Left", "Channels", "When"].map((h) => (
            <Typography
              key={h}
              sx={{
                fontFamily: HEADING_FONT,
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: colors.textMuted,
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {histLoading ? (
          <TableSkeleton rows={histPageSize || 8} columns={5} showAvatar={false} />
        ) : history.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center", color: colors.textMuted }}>
            <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 14, fontWeight: 600, color: colors.text }}>
              No alerts raised yet
            </Typography>
            <Typography sx={{ fontSize: 13, mt: 0.5 }}>
              Alerts appear here once a domain crosses a threshold
            </Typography>
          </Box>
        ) : (
          pagedHistory.map((h, idx) => {
            const band = colorForDays(h.daysLeft);
            return (
              <Box
                key={h.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: HIST_COLS },
                  gap: { xs: 0.5, md: 2 },
                  px: 3,
                  py: 1.5,
                  alignItems: "center",
                  borderBottom:
                    idx < pagedHistory.length - 1
                      ? `1px solid ${colors.divider}`
                      : "none",
                  "&:hover": { background: colors.hover },
                  transition: "background 0.18s ease",
                }}
              >
                <Typography
                  sx={{ fontFamily: HEADING_FONT, fontSize: 14, fontWeight: 600, color: colors.text }}
                >
                  {h.domainName}
                </Typography>
                <Box>
                  <Chip
                    label={`${h.thresholdDays}d`}
                    size="small"
                    sx={{
                      fontFamily: HEADING_FONT,
                      height: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      color: band.hex,
                      background: band.bg,
                    }}
                  />
                </Box>
                <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 13, color: colors.textMuted }}>
                  {expiresInLabel(h.daysLeft)}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {(h.channelsSent || []).length === 0 ? (
                    <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 12, color: colors.textFaint }}>
                      none configured
                    </Typography>
                  ) : (
                    (h.channelsSent || []).map((c) => (
                      <Chip
                        key={c}
                        label={c}
                        size="small"
                        sx={{
                          fontFamily: HEADING_FONT,
                          height: 20,
                          fontSize: 10,
                          textTransform: "capitalize",
                          color: CHANNEL_COLOR,
                          background: "rgba(59, 130, 246, 0.12)",
                        }}
                      />
                    ))
                  )}
                  {(h.channelsFailed || []).map((c) => (
                    <Tooltip key={c} title="Delivery failed">
                      <Chip
                        label={c}
                        size="small"
                        sx={{
                          fontFamily: HEADING_FONT,
                          height: 20,
                          fontSize: 10,
                          textTransform: "capitalize",
                          color: "#ef4444",
                          background: "rgba(239, 68, 68, 0.12)",
                        }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
                <Typography sx={{ fontFamily: HEADING_FONT, fontSize: 12, color: colors.textMuted }}>
                  {h.createdAt ? String(h.createdAt).replace("T", " ").slice(0, 16) : "—"}
                </Typography>
              </Box>
            );
          })
        )}
        </Box>

        {!histLoading && histTotal > histPageSize && (
          <PaginationFooter
            page={histSafePage}
            pageSize={histPageSize}
            total={histTotal}
            onPageChange={setHistPage}
            onPageSizeChange={setHistPageSize}
          />
        )}
      </FlatCard>
    </DashboardLayout>
  );
}

export default ExpiryAlerts;
